import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Button, Card, Spinner } from '@easydent/shared-ui';
import { createOperator, listOperators, removeOperator, type OperatorSummary } from '@/lib/api';

/**
 * The platform's own operator accounts.
 *
 * <p>Before this screen the only way to get a SuperAdmin was the startup seeder reading one email and
 * password out of Vault — and only when that account was missing. Production therefore ran with a
 * single operator login, 2FA enforced, and nobody who could let you back in.</p>
 *
 * <p>No password field, deliberately: the new operator is emailed a link and chooses their own, so
 * the person creating the account never knows how to sign into it.</p>
 */
const OperatorsPage: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<OperatorSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const load = () =>
    listOperators()
      .then(setRows)
      .catch(() => setError(t('operators.loadError', 'Could not load the operator list.')));

  useEffect(() => { load(); }, []);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await createOperator(email.trim(), firstName.trim(), lastName.trim());
      setNotice(t('operators.invited', 'Invitation sent. They set their own password from the link.'));
      setEmail(''); setFirstName(''); setLastName('');
      await load();
    } catch (err) {
      const data = (err as { response?: { data?: { detail?: string; title?: string } } })?.response?.data;
      setError(data?.detail ?? data?.title ?? t('operators.addError', 'Could not add that operator.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: OperatorSummary) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('operators.confirmRemove', 'Remove {{email}} from the platform?', { email: row.email }))) return;
    setError(null);
    setNotice(null);
    try {
      await removeOperator(row.id);
      await load();
    } catch (err) {
      const data = (err as { response?: { data?: { detail?: string; title?: string } } })?.response?.data;
      setError(data?.detail ?? data?.title ?? t('operators.removeError', 'Could not remove that operator.'));
    }
  };

  const field = 'w-full rounded-lg border border-line-strong px-3 py-2 text-sm focus:border-brand-500 focus:outline-none';
  const date = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-body">{t('operators.title', 'Operators')}</h1>

      {error && <Alert variant="error">{error}</Alert>}
      {notice && <Alert variant="success">{notice}</Alert>}

      <Card>
        <div className="mb-3 text-sm font-semibold text-body-strong">
          {t('operators.addTitle', 'Add an operator')}
        </div>
        <p className="mb-4 text-sm text-muted">
          {t('operators.addHint', 'They receive an email and choose their own password. An operator account can see every clinic on the platform.')}
        </p>
        <form onSubmit={add} className="grid gap-3 sm:grid-cols-4">
          <input
            type="email" required placeholder={t('operators.email', 'Email')} aria-label={t('operators.email', 'Email')}
            className={field} value={email} onChange={(e) => setEmail(e.target.value)}
          />
          <input
            required placeholder={t('operators.firstName', 'First name')} aria-label={t('operators.firstName', 'First name')}
            className={field} value={firstName} onChange={(e) => setFirstName(e.target.value)}
          />
          <input
            required placeholder={t('operators.lastName', 'Last name')} aria-label={t('operators.lastName', 'Last name')}
            className={field} value={lastName} onChange={(e) => setLastName(e.target.value)}
          />
          <Button type="submit" disabled={busy}>
            {busy ? t('operators.adding', 'Sending…') : t('operators.add', 'Send invitation')}
          </Button>
        </form>
      </Card>

      {rows === null ? (
        <div className="flex justify-center p-10"><Spinner size="lg" /></div>
      ) : (
        <Card>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-subtle-ink">
                <th className="py-2">{t('operators.name', 'Name')}</th>
                <th className="py-2">{t('operators.email', 'Email')}</th>
                <th className="py-2">{t('operators.state', 'State')}</th>
                <th className="py-2">{t('operators.lastSeen', 'Last seen')}</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} className="border-b border-line-faint last:border-0">
                  <td className="py-2 text-body">{o.firstName} {o.lastName}</td>
                  <td className="py-2 text-muted">{o.email}</td>
                  <td className="py-2">
                    {!o.hasPassword
                      ? <Badge variant="warning">{t('operators.pending', 'Invitation pending')}</Badge>
                      : o.twoFactorEnabled
                        ? <Badge variant="success">{t('operators.twoFactorOn', '2FA on')}</Badge>
                        // The widest-reaching credential on the platform behind a password alone.
                        // Worth saying out loud on the one screen that lists them.
                        : <Badge variant="warning">{t('operators.twoFactorOff', 'No 2FA')}</Badge>}
                  </td>
                  <td className="py-2 text-muted">{date(o.lastSeenAt)}</td>
                  <td className="py-2 text-right">
                    <button
                      type="button" onClick={() => remove(o)}
                      className="text-sm text-danger-ink hover:underline"
                    >
                      {t('operators.remove', 'Remove')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

export default OperatorsPage;
